// Offline "QA mode" for the email verification flow + account-page backup
// email screens — lets a reviewer walk every screen (signin methods, which
// prompt is due, the reauth modal, error states) without hitting the real API.
//
// Always on — this is deployed to its own dedicated review URL, so there's
// no real-user traffic to gate it away from.
const STORAGE_KEY = 'plickers:emailVerificationQA'

// fixed address used whenever QA mode seeds a backup email (the panel's
// checkbox), so it's always recognizable as mock data
export const MOCK_BACKUP_EMAIL = 'backup-email@plickers.com'

const defaultState = {
  password: true,
  externalSignin: 'none', // 'none' | 'google' | 'apple' — an account can't have both
  primaryState: 'none', // 'none' | 'verifyDue' | 'affirmDue' — mutually exclusive
  hasBackupEmail: false,
  // the address actually typed into a verified backup-email form, so the
  // mocked user.backupEmail shows it instead of resetting to
  // MOCK_BACKUP_EMAIL — null (with hasBackupEmail set) means the mock default
  backupEmail: null,
  // meaning depends on hasBackupEmail: with no backup email 'due' means
  // addBackup is due; with one set, 'due' means affirmBackup is due
  backupState: 'none', // 'none' | 'due'
  requireReauth: false,
  // mirrors the real user.meta.emailVerificationPromptState.addBackupDismissCount
  // — lets QA mode test "Don't ask me again" appearing past its threshold,
  // either by clicking "Not now" for real or overriding it in the QA panel
  addBackupDismissCount: 0,
  // whether success states ("Verified!", "Confirmed!", ...) auto-advance to
  // the next step / close after a few seconds, like the real flow — off by
  // default so a reviewer can look at each success state without racing it
  autoAdvance: false,
  // which variant of the email box's edit button to show — see
  // EMAIL_EDIT_BUTTON_STYLES, design exploration only
  editButtonStyle: 'pencilText',
  // whether the verifyPrimary offer shows its description paragraph under
  // the title — design exploration only, hidden by default
  showVerifyPrimaryDescription: false,
}

// the email box edit button variants the QA panel can switch between
export const EMAIL_EDIT_BUTTON_STYLES = {
  pencil: 'pencil icon',
  text: '"Edit" text (no border)',
  bordered: '"Edit" button (with border)',
  pencilText: 'pencil + "Edit"',
}

// fired on window whenever QA state changes, so already-mounted screens
// (e.g. the edit button style) can update live without a reload
export const EMAIL_VERIFICATION_QA_CHANGE_EVENT = 'emailVerificationQAChange'

function notifyChange() {
  try {
    window.dispatchEvent(new Event(EMAIL_VERIFICATION_QA_CHANGE_EVENT))
  } catch (e) {
    // ignore
  }
}

// Kept in memory rather than localStorage (unlike the rest of QA state) so it
// actually resets on a page reload, matching qaCreateEmailVerification's "once
// per QA session" comment — persisting it would leave a stale reauthed:true
// around from an earlier test, silently defeating the requireReauth toggle.
let reauthed = false

export function deriveSigninMethods(qa) {
  return {
    password: !!qa.password,
    google: qa.externalSignin === 'google',
    apple: qa.externalSignin === 'apple',
  }
}

// what the mocked user.backupEmail should be for the given QA state
export function deriveBackupEmail(qa) {
  if (!qa.hasBackupEmail) return null
  return qa.backupEmail || MOCK_BACKUP_EMAIL
}

export function derivePrompts(qa) {
  return {
    verifyPrimary: qa.primaryState === 'verifyDue',
    affirmPrimary: qa.primaryState === 'affirmDue',
    addBackup: !qa.hasBackupEmail && qa.backupState === 'due',
    affirmBackup: qa.hasBackupEmail && qa.backupState === 'due',
  }
}

function readState() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : {}
  } catch (e) {
    return {}
  }
}

function writeState(partial) {
  const next = { ...defaultState, ...readState(), ...partial }
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
  } catch (e) {
    // ignore (private browsing, storage disabled, ...) — QA mode just won't persist
  }
  return next
}

// Always true — kept as a function (rather than a plain export) so call
// sites read the same either way if this ever needs gating again.
export function isEmailVerificationQAEnabled() {
  return typeof window !== 'undefined'
}

// the real flow always auto-advances; QA mode only does when toggled on
export function isEmailVerificationAutoAdvanceEnabled() {
  if (!isEmailVerificationQAEnabled()) return true
  return !!getEmailVerificationQAState().autoAdvance
}

export function isVerifyPrimaryDescriptionShown() {
  if (!isEmailVerificationQAEnabled()) return defaultState.showVerifyPrimaryDescription
  return !!getEmailVerificationQAState().showVerifyPrimaryDescription
}

export function getEmailEditButtonStyle() {
  if (!isEmailVerificationQAEnabled()) return defaultState.editButtonStyle
  const { editButtonStyle } = getEmailVerificationQAState()
  return EMAIL_EDIT_BUTTON_STYLES[editButtonStyle] ? editButtonStyle : defaultState.editButtonStyle
}

export function getEmailVerificationQAState() {
  return { ...defaultState, ...readState(), reauthed }
}

export function setEmailVerificationQAState(partial) {
  const { reauthed: nextReauthed, ...persisted } = partial
  if ('reauthed' in partial) {
    reauthed = nextReauthed
  }
  const next = writeState(persisted)
  notifyChange()
  return { ...next, reauthed }
}

export function resetEmailVerificationQAState() {
  try {
    window.localStorage.removeItem(STORAGE_KEY)
  } catch (e) {
    // ignore
  }
  reauthed = false
  notifyChange()
  return { ...defaultState, reauthed }
}

// how long every QA-mode mocked request takes to settle — a real round trip
// isn't instant, and resolving right away would skip straight past loading
// states (button spinners, disabled links) so QA never sees them
const MOCK_REQUEST_DELAY_MS = 1500

export function mockRequestDelay() {
  return new Promise((resolve) => setTimeout(resolve, MOCK_REQUEST_DELAY_MS))
}

// A fake createEmailVerification record — shaped like the real API response.
// resendAvailableAt is 10s out (rather than a real-world cooldown) so QA can
// see the full "Sent!" -> "Available in Ns" -> "Resend" sequence quickly.
export function fakeVerificationRecord(purpose) {
  const now = Date.now()
  return {
    id: `qa-${purpose}-${now}`,
    purpose,
    expiresAt: new Date(now + 10 * 60 * 1000).toISOString(),
    resendAvailableAt: new Date(now + 10 * 1000).toISOString(),
  }
}

// createEmailVerification only reauths for updatePrimary/upsertBackup, mirroring
// the real endpoint (verifyPrimary never needs it) — see emailVerification.js's
// `create`. Returns a rejection shaped like the real 403 reauth_required so the
// existing container error-handling (checking err.response.status) just works.
export function reauthRequiredError() {
  const error = new Error('reauth_required')
  error.response = { status: 403 }
  return error
}

// Type one of these into the verify-code screen in QA mode to force a
// specific failure instead of succeeding — lets a reviewer reach every error
// state getErrorMessageForRequest knows about for 'verifyEmailVerificationCode'
// without a live backend. Any other 6 digits (including the real-looking ones
// QA mode doesn't actually check) succeed.
export const MOCK_VERIFICATION_CODES = {
  INCORRECT: '000000',
  EXPIRED: '111111',
  RATE_LIMITED: '222222',
  NO_LONGER_PENDING: '333333',
  SERVER_ERROR: '999999',
}

const MOCK_VERIFICATION_CODE_STATUSES = {
  [MOCK_VERIFICATION_CODES.INCORRECT]: 422, // wrong code, attempts remain
  [MOCK_VERIFICATION_CODES.EXPIRED]: 419, // past the record's expiresAt
  [MOCK_VERIFICATION_CODES.RATE_LIMITED]: 429, // maxAttempts/maxSends exhausted
  [MOCK_VERIFICATION_CODES.NO_LONGER_PENDING]: 412, // discarded by a newer request, or already used
  [MOCK_VERIFICATION_CODES.SERVER_ERROR]: 500, // exercises the generic fallback message
}

// Type this as the password in QA mode's reauth modal to get the real API's
// 412 wrong-password rejection — any other password succeeds. Only checked by
// the mocks that take a password (currently removeBackupEmail).
export const MOCK_INCORRECT_PASSWORD = 'wrongpassword'

export function mockIncorrectPasswordError(password) {
  if (password !== MOCK_INCORRECT_PASSWORD) return null
  const error = new Error('qa mock: incorrect password 412')
  error.response = { status: 412 }
  return error
}

// Returns an error shaped like the real API's rejection for the given status,
// or null if `code` isn't one of the magic ones.
export function mockVerificationCodeError(code) {
  const status = MOCK_VERIFICATION_CODE_STATUSES[code]
  if (!status) return null
  const error = new Error(`qa mock: verify ${status}`)
  error.response = { status }
  return error
}
